import { useState } from "react";
import { motion } from "framer-motion";
import { Send, Phone, Mail, MapPin, Check } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    // Simulate form submission
    await new Promise((resolve) => setTimeout(resolve, 1000));
    
    setIsLoading(false);
    setIsSubmitted(true);
    setFormData({ name: "", email: "", phone: "", message: "" });
    
    // Reset success message after 5 seconds
    setTimeout(() => setIsSubmitted(false), 5000);
  };

  const contactInfo = [
    {
      icon: Phone,
      label: "Téléphone",
      value: "+213 668 41 87 16",
      href: "tel:+213668418716",
    },
    {
      icon: Mail,
      label: "Email",
      value: "contact@parfumalgerie.shop",
      href: "mailto:contact@parfumalgerie.shop",
    },
    {
      icon: MapPin,
      label: "Adresse",
      value: "Alger, Algérie",
      href: "#",
    },
  ];

  return (
    <section id="contact" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left: Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="font-montserrat text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-6 block">
              Contactez-nous
            </span>

            <h2 className="font-playfair text-4xl md:text-5xl font-medium tracking-tight text-[#1A1A1A] mb-6 leading-tight">
              Une Question ?
              <br />
              <span className="italic font-cormorant">Parlons-en</span>
            </h2>

            <p className="font-montserrat text-base md:text-lg text-[#666] leading-relaxed mb-10">
              Notre équipe est à votre disposition pour répondre à toutes vos
              questions concernant nos parfums, les livraisons ou les commandes
              spéciales.
            </p>

            {/* Contact Details */}
            <div className="space-y-6">
              {contactInfo.map((item, index) => (
                <motion.a
                  key={item.label}
                  href={item.href}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  className="flex items-start gap-4 group"
                  data-testid={`contact-${item.label.toLowerCase()}`}
                >
                  <div className="w-12 h-12 bg-[#FAFAFA] rounded-full flex items-center justify-center group-hover:bg-[#D4AF37] transition-colors">
                    <item.icon
                      size={20}
                      strokeWidth={1.5}
                      className="text-[#1A1A1A] group-hover:text-white transition-colors"
                    />
                  </div>
                  <div>
                    <span className="font-montserrat text-xs uppercase tracking-[0.15em] text-[#999] block mb-1">
                      {item.label}
                    </span>
                    <span className="font-montserrat text-base text-[#1A1A1A] group-hover:text-[#D4AF37] transition-colors">
                      {item.value}
                    </span>
                  </div>
                </motion.a>
              ))}
            </div>

            {/* Working Hours */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="mt-10 p-6 bg-[#FAFAFA] rounded-sm"
            >
              <h4 className="font-playfair text-lg text-[#1A1A1A] mb-3">
                Horaires de Service
              </h4>
              <p className="font-montserrat text-sm text-[#666]">
                Samedi - Jeudi: 9h00 - 18h00
                <br />
                Vendredi: Fermé
              </p>
            </motion.div>
          </motion.div>

          {/* Right: Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="bg-[#FAFAFA] p-8 md:p-10 rounded-sm">
              <h3 className="font-playfair text-2xl text-[#1A1A1A] mb-6">
                Envoyez-nous un Message
              </h3>

              {isSubmitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-12"
                >
                  <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check size={32} className="text-white" />
                  </div>
                  <h4 className="font-playfair text-xl text-[#1A1A1A] mb-2">
                    Message Envoyé !
                  </h4>
                  <p className="font-montserrat text-sm text-[#666]">
                    Nous vous répondrons dans les plus brefs délais.
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="font-montserrat text-xs uppercase tracking-[0.15em] text-[#666] mb-2 block">
                      Nom Complet
                    </label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Votre nom"
                      className="form-input w-full rounded-none border-black/10 focus:border-[#D4AF37] focus:ring-0"
                      data-testid="contact-name-input"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="font-montserrat text-xs uppercase tracking-[0.15em] text-[#666] mb-2 block">
                        Email
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="votre@email.com"
                        className="form-input w-full rounded-none border-black/10 focus:border-[#D4AF37] focus:ring-0"
                        data-testid="contact-email-input"
                      />
                    </div>
                    <div>
                      <label className="font-montserrat text-xs uppercase tracking-[0.15em] text-[#666] mb-2 block">
                        Téléphone
                      </label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+213 XXX XXX XXX"
                        className="form-input w-full rounded-none border-black/10 focus:border-[#D4AF37] focus:ring-0"
                        data-testid="contact-phone-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="font-montserrat text-xs uppercase tracking-[0.15em] text-[#666] mb-2 block">
                      Message
                    </label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      placeholder="Comment pouvons-nous vous aider ?"
                      className="form-input w-full rounded-none border-black/10 focus:border-[#D4AF37] focus:ring-0 resize-none"
                      data-testid="contact-message-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full btn-primary rounded-full py-4 flex items-center justify-center gap-3"
                    data-testid="contact-submit-btn"
                  >
                    {isLoading ? (
                      <span className="animate-pulse">Envoi en cours...</span>
                    ) : (
                      <>
                        Envoyer le Message
                        <Send size={16} />
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
